/**
 * BuilderLegRow Component
 * Individual leg display within the parlay builder panel
 * Features expandable context, chart button, and remove functionality
 */

import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { teamSideLegText } from '@parlay-picker/shared/statLabels';
import { gameChartPropType } from '@parlay-picker/shared/statType';
import { findTeam, getTeamAbbr } from '@parlay-picker/shared/teams';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { cn } from '@utils/cn';
import { formatPropType } from '@utils/propUtils';
import type { ChartClickData } from '@views/picks/components/ParlayCard';
import { formatPropDisplay, usePlayerHeadshotUrl } from '@views/picks/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { BarChart3, ChevronDown, ClipboardList, User, X } from 'lucide-react';
import { type MouseEvent, type ReactElement, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

interface BuilderLegRowProps {
  leg: BuilderLeg;
  index: number;
  onRemove: (legId: string) => void;
  /** Disable interactions (e.g., while analyzing) */
  disabled?: boolean;
  /** Chart click handler - when provided, shows chart button for legs with stats data */
  onChartClick?: (data: ChartClickData) => void;
  /** Per-leg confidence grade from AI analysis */
  analysisGrade?: { grade: 'A' | 'B' | 'C' | 'D' | 'F'; note: string };
  /** ML anchor badge — this leg appears in multiple optimizer parlays */
  isAnchor?: boolean;
  /** ML score (0-100) from ml_prop_scores */
  mlScore?: number | null;
  /** CLV indicator — line movement since opening */
  clvPoints?: number | null;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if this is a totals bet (game total, not spread/moneyline)
 */
function isTotalsBet(value: string | null | undefined): boolean {
  if (!value) return false;
  const normalized = value.toLowerCase();
  return normalized === 'totals' || normalized === 'total';
}

/**
 * Parse a matchup string like "Hawks @ Celtics" or "Lakers vs Rockets"
 * Returns both teams' data if found
 */
function parseMatchupTeams(
  matchupStr: string | null | undefined,
  leagueKey: LeagueKey,
): {
  away: ReturnType<typeof findTeam>;
  home: ReturnType<typeof findTeam>;
} | null {
  if (!matchupStr) return null;

  // Try to split on common separators: "vs", "vs.", "@", "at"
  const match = matchupStr.match(/(.+?)\s*(?:vs\.?|@|at)\s*(.+)/i);
  if (!match) return null;

  const awayTeam = findTeam(match[1].trim(), leagueKey);
  const homeTeam = findTeam(match[2].trim(), leagueKey);

  if (!awayTeam && !homeTeam) return null;

  return { away: awayTeam, home: homeTeam };
}

/**
 * Build display prop string from leg data
 */
export function buildPropDisplay(leg: BuilderLeg): string {
  if (leg.overUnder && leg.line !== null) {
    // For totals, just show "Over 226.5" without redundant "Total" suffix
    if (isTotalsBet(leg.statType)) {
      return `${leg.overUnder} ${leg.line}`;
    }
    // For player props, show stat type (e.g., "Over 25.5 Points")
    return `${leg.overUnder} ${leg.line} ${formatPropType(leg.statType)}`;
  }
  return teamSideLegText(leg.team, leg.line, leg.marketKey);
}

// =============================================================================
// Fallback Components
// =============================================================================

// TeamLogoFallback imported from shared component
import { TeamLogoFallback } from '../ui/TeamLogo';

function HeadshotFallback(): ReactElement {
  return (
    <div className="w-8 h-8 rounded-full bg-theme-surface flex items-center justify-center">
      <User className="w-5 h-5 text-theme-text-tertiary" />
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

/** Grade badge color styling */
function getGradeStyle(grade: string): string {
  switch (grade) {
    case 'A':
      return 'bg-green-900/50 text-green-400 border-green-500/30';
    case 'B':
      return 'bg-teal-900/50 text-teal-400 border-teal-500/30';
    case 'C':
      return 'bg-yellow-900/50 text-yellow-400 border-yellow-500/30';
    case 'D':
      return 'bg-orange-900/50 text-orange-400 border-orange-500/30';
    case 'F':
      return 'bg-red-900/50 text-red-400 border-red-500/30';
    default:
      return 'bg-theme-surface text-theme-text-tertiary';
  }
}

export function BuilderLegRow({
  leg,
  index,
  onRemove,
  disabled = false,
  onChartClick,
  analysisGrade,
  isAnchor,
  mlScore,
  clvPoints,
}: BuilderLegRowProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [awayLogoError, setAwayLogoError] = useState(false);
  const [homeLogoError, setHomeLogoError] = useState(false);
  const [headshotError, setHeadshotError] = useState(false);

  const headshotUrl = usePlayerHeadshotUrl(leg.player, leg.leagueKey);

  // Reset headshot error when URL changes (e.g., when dynamic player ID map loads)
  useEffect(() => {
    setHeadshotError(false);
  }, []);

  const isGameProp = isTeamBet(leg.marketKey) || isTeamBet(leg.statType);
  const isTotals = isTotalsBet(leg.marketKey) || isTotalsBet(leg.statType);

  // For totals, parse "Team A vs Team B" format to get both teams
  const matchupTeams = isTotals ? parseMatchupTeams(leg.team, leg.leagueKey) : null;

  // For single-team bets (spread/moneyline), try findTeam for normalization
  const teamData = leg.team && !isTotals ? findTeam(leg.team, leg.leagueKey) : undefined;

  // Opponent abbreviation for "vs OPP" display
  const opponentAbbr = leg.opponentTeam ? getTeamAbbr(leg.opponentTeam, leg.leagueKey) : null;

  const propDisplay = buildPropDisplay(leg);

  // Check if there's any context to show
  const hasContext =
    leg.aiAnalysis ||
    leg.isB2b !== null ||
    leg.blowoutRisk ||
    leg.matchupNote ||
    leg.recentForm ||
    leg.injuryContext ||
    leg.primaryEdge ||
    leg.supportingFactors?.length ||
    leg.riskFactors?.length ||
    leg.confidenceBreakdown;

  // Show chart button for any leg where we can fetch/show data (player name exists, or it's a game prop with team)
  const canShowChart = !isGameProp ? !!leg.player : isTotals ? !!matchupTeams : !!leg.team;

  // Handle chart button click
  const handleChartClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    if (!onChartClick) return;

    if (isGameProp) {
      // Game prop chart data (may need to be fetched by parent)
      onChartClick({
        leagueKey: leg.leagueKey,
        isGameProp: true,
        teamName: leg.team || 'Team',
        prop: formatPropDisplay(propDisplay) || propDisplay,
        teamRecentGames: leg.teamRecentGames as ChartClickData['teamRecentGames'],
        teamH2hGames: leg.teamH2hGames as ChartClickData['teamH2hGames'],
        propLine: leg.line,
        propType: gameChartPropType(leg.statType ?? '') ?? 'spread',
        opponentAbbr: leg.opponentTeam || undefined,
        overUnder: leg.overUnder,
        opponentTeamName: leg.opponentTeamName || leg.opponentTeam || undefined,
        opponentTeamRecentGames:
          leg.opponentTeamRecentGames as ChartClickData['opponentTeamRecentGames'],
        odds: leg.odds ?? undefined,
        marketKey: leg.marketKey || undefined,
      });
    } else {
      // Player prop chart data (may need to be fetched by parent)
      onChartClick({
        leagueKey: leg.leagueKey,
        playerName: leg.player || undefined,
        playerTeamName: leg.team || undefined,
        prop: formatPropDisplay(propDisplay) || propDisplay,
        recentGames: leg.recentGames as ChartClickData['recentGames'],
        h2hGames: leg.h2hGames as ChartClickData['h2hGames'],
        propLine: leg.line,
        statKey: leg.statType || undefined,
        opponentAbbr: leg.opponentTeam || undefined,
        overUnder: leg.overUnder,
        odds: leg.odds ?? undefined,
        marketKey: leg.marketKey || undefined,
      });
    }
  };

  // Handle row click to toggle expansion
  const handleRowClick = (): void => {
    if (hasContext) {
      setIsExpanded(!isExpanded);
    }
  };

  // Handle remove button click (stop propagation to prevent row expansion)
  const handleRemoveClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onRemove(leg.id);
  };

  // Shared expanded context content
  const renderExpandedContext = (): ReactElement => (
    <div className="px-3 pb-2 pt-1 border-t border-theme-border/50 space-y-1 text-xs">
      {/* AI Analysis */}
      {leg.aiAnalysis && (
        <div className="flex items-start gap-1.5 text-theme-primary">
          <span>🤖</span>
          <span className="flex-1">{leg.aiAnalysis}</span>
        </div>
      )}

      {/* AI Confidence */}
      {leg.aiConfidence && (
        <div className="flex items-center gap-1.5">
          <span>⚡</span>
          <span
            className={`capitalize ${
              leg.aiConfidence === 'high'
                ? 'text-green-400'
                : leg.aiConfidence === 'medium'
                  ? 'text-yellow-400'
                  : 'text-theme-text-tertiary'
            }`}
          >
            {leg.aiConfidence} confidence
          </span>
        </div>
      )}

      {/* Situational context row */}
      <div className={cn('flex flex-wrap gap-2', 'text-theme-text-secondary')}>
        {/* B2B status */}
        {leg.isB2b !== null && (
          <span className={leg.isB2b ? 'text-orange-400' : 'text-green-400'}>
            {leg.isB2b ? 'B2B' : 'Not B2B'}
          </span>
        )}

        {/* Blowout risk */}
        {leg.blowoutRisk && (
          <span
            className={
              leg.blowoutRisk === 'high'
                ? 'text-red-400'
                : leg.blowoutRisk === 'medium'
                  ? 'text-yellow-400'
                  : 'text-green-400'
            }
          >
            {leg.blowoutRisk} blowout risk
          </span>
        )}

        {/* Recent form */}
        {leg.recentForm && <span>{leg.recentForm}</span>}
      </div>

      {/* Matchup note */}
      {leg.matchupNote && <div className={'text-theme-text-secondary'}>📊 {leg.matchupNote}</div>}

      {/* Injury context */}
      {leg.injuryContext && <div className="text-orange-300">🏥 {leg.injuryContext}</div>}

      {/* === ENHANCED EDGE DISPLAY (PicksView-style) === */}

      {/* Primary Edge Box */}
      {leg.primaryEdge && (
        <div className="rounded-lg p-2 mt-2 bg-theme-primary/10 border border-theme-primary/30">
          <div
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider mb-1',
              'text-theme-primary',
            )}
          >
            Primary Edge
          </div>
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <div className="flex items-center gap-1">
              <span className={'text-theme-text-secondary'}>{leg.primaryEdge.metric} Avg:</span>
              <span className={cn('font-medium', 'text-theme-text')}>
                {leg.primaryEdge.playerValue}
              </span>
            </div>
            <span className={'text-theme-border'}>|</span>
            <div className="flex items-center gap-1">
              <span className={'text-theme-text-secondary'}>Line:</span>
              <span className={cn('font-medium', 'text-theme-text')}>{leg.primaryEdge.line}</span>
            </div>
            <span className={'text-theme-border'}>|</span>
            <div className="flex items-center gap-1">
              <span className={'text-theme-text-secondary'}>Edge:</span>
              <span
                className={`font-bold ${(leg.primaryEdge.edge || 0) > 0 ? 'text-green-400' : 'text-red-400'}`}
              >
                {(leg.primaryEdge.edge || 0) > 0 ? '+' : ''}
                {leg.primaryEdge.edge}
              </span>
              <span className={cn('text-[10px]', 'text-theme-text-tertiary')}>
                ({(leg.primaryEdge.edgePercent || 0) > 0 ? '+' : ''}
                {leg.primaryEdge.edgePercent?.toFixed(1)}%)
              </span>
            </div>
          </div>
          {/* Player average display */}
          {leg.average && (
            <div className={cn('text-[10px] mt-1', 'text-theme-text-secondary')}>
              Recent average: <span className="text-green-400 font-medium">{leg.average}</span>
            </div>
          )}
        </div>
      )}

      {/* Supporting Factors */}
      {leg.supportingFactors && leg.supportingFactors.length > 0 && (
        <div
          className={cn(
            'rounded-lg p-2 mt-2',
            'bg-theme-success/10 border border-theme-success/30',
          )}
        >
          <div
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider mb-1',
              'text-theme-success',
            )}
          >
            Supporting Factors
          </div>
          <div className="space-y-1">
            {leg.supportingFactors.map((factor, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[11px]">
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    factor.impact === 'positive'
                      ? 'bg-theme-success'
                      : factor.impact === 'negative'
                        ? 'bg-red-400'
                        : 'bg-theme-text-tertiary'
                  }`}
                />
                <span className={'text-theme-text-secondary'}>{factor.label}:</span>
                <span className={cn('font-medium', 'text-theme-text')}>{factor.value}</span>
                {factor.detail && (
                  <span className={'text-theme-text-tertiary'}>({factor.detail})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {leg.riskFactors && leg.riskFactors.length > 0 && (
        <div
          className={cn(
            'rounded-lg p-2 mt-2',
            'bg-theme-warning/10 border border-theme-warning/30',
          )}
        >
          <div
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider mb-1',
              'text-theme-warning',
            )}
          >
            Risk Factors
          </div>
          <div className="space-y-1">
            {leg.riskFactors.map((risk, idx) => (
              <div key={idx} className="flex items-center gap-2 text-[11px]">
                <span
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                    risk.severity === 'high'
                      ? 'bg-red-900/50 text-red-400'
                      : risk.severity === 'medium'
                        ? 'bg-yellow-900/50 text-yellow-400'
                        : 'bg-theme-surface text-theme-text-tertiary'
                  }`}
                >
                  {risk.severity}
                </span>
                <span className={'text-theme-text-secondary'}>{risk.label}:</span>
                <span className={'text-theme-text-tertiary'}>{risk.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confidence Breakdown */}
      {leg.confidenceBreakdown && (
        <div className={cn('rounded-lg p-2 mt-2', 'bg-purple-500/10 border border-purple-500/30')}>
          <div
            className={cn(
              'text-[10px] font-semibold uppercase tracking-wider mb-2',
              'text-purple-300',
            )}
          >
            Confidence Breakdown
          </div>
          <div className="space-y-1.5">
            {[
              {
                label: 'Statistical Edge',
                value: leg.confidenceBreakdown.statisticalEdge,
              },
              {
                label: 'Matchup Factor',
                value: leg.confidenceBreakdown.matchupFactor,
              },
              {
                label: 'Consistency',
                value: leg.confidenceBreakdown.consistency,
              },
              {
                label: 'Game Script',
                value: leg.confidenceBreakdown.gameScript,
              },
            ].map((factor) => (
              <div key={factor.label} className="flex items-center gap-2 text-[11px]">
                <span className={cn('w-24 shrink-0', 'text-theme-text-secondary')}>
                  {factor.label}:
                </span>
                <div
                  className={cn('flex-1 h-1.5 rounded-full overflow-hidden', 'bg-theme-surface')}
                >
                  <div
                    className={`h-full rounded-full ${
                      (factor.value || 0) >= 0.7
                        ? 'bg-theme-success'
                        : (factor.value || 0) >= 0.5
                          ? 'bg-theme-warning'
                          : 'bg-theme-danger'
                    }`}
                    style={{ width: `${(factor.value || 0) * 100}%` }}
                  />
                </div>
                <span className={cn('w-8 text-right', 'text-theme-text-secondary')}>
                  {Math.round((factor.value || 0) * 100)}%
                </span>
              </div>
            ))}
            <div
              className={cn(
                'flex items-center gap-2 text-[11px] pt-1 border-t mt-1',
                'border-purple-500/30',
              )}
            >
              <span className="font-medium text-purple-300">Overall:</span>
              <span
                className={`font-bold ${
                  (leg.confidenceBreakdown.overall || 0) >= 0.7
                    ? 'text-green-400'
                    : (leg.confidenceBreakdown.overall || 0) >= 0.5
                      ? 'text-yellow-400'
                      : 'text-red-400'
                }`}
              >
                {Math.round((leg.confidenceBreakdown.overall || 0) * 100)}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden',
        'bg-theme-surface/60 border border-theme-border/30 rebrand-builder-leg',
      )}
    >
      {/* Main row - clickable when has expandable context */}
      <div
        className={cn(
          'flex items-center gap-2 p-2',
          hasContext && 'cursor-pointer hover:bg-theme-surface-hover transition-colors',
        )}
        onClick={handleRowClick}
      >
        {/* Index number / grade badge */}
        {analysisGrade ? (
          <span
            className={cn(
              'text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded border',
              getGradeStyle(analysisGrade.grade),
            )}
            title={analysisGrade.note}
          >
            {analysisGrade.grade}
          </span>
        ) : (
          <span className={cn('text-xs w-4', 'text-theme-text-tertiary')}>{index + 1}</span>
        )}

        {/* Logo/Headshot */}
        <div className="shrink-0">
          {isTotals && matchupTeams ? (
            /* Totals: Show both team logos */
            <div className="flex items-center gap-0.5">
              {matchupTeams.away && !awayLogoError ? (
                <img
                  src={matchupTeams.away.logo}
                  alt={matchupTeams.away.name}
                  className="w-6 h-6 object-contain"
                  onError={() => setAwayLogoError(true)}
                />
              ) : (
                <TeamLogoFallback abbr={matchupTeams.away?.abbr || '?'} size="md" />
              )}
              {matchupTeams.home && !homeLogoError ? (
                <img
                  src={matchupTeams.home.logo}
                  alt={matchupTeams.home.name}
                  className="w-6 h-6 object-contain"
                  onError={() => setHomeLogoError(true)}
                />
              ) : (
                <TeamLogoFallback abbr={matchupTeams.home?.abbr || '?'} size="md" />
              )}
            </div>
          ) : isGameProp ? (
            /* Spread/Moneyline: Show single team logo */
            teamData && !logoError ? (
              <img
                src={'logo' in teamData ? teamData.logo : ''}
                alt={leg.team || ''}
                className="w-8 h-8 object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <TeamLogoFallback
                abbr={
                  teamData && 'abbr' in teamData ? teamData.abbr : leg.team?.substring(0, 3) || '?'
                }
                size="md"
              />
            )
          ) : /* Player prop: Show headshot */
          headshotUrl && !headshotError ? (
            <img
              src={headshotUrl}
              alt={leg.player || ''}
              className="w-8 h-8 object-cover rounded-full"
              onError={() => setHeadshotError(true)}
            />
          ) : (
            <HeadshotFallback />
          )}
        </div>

        {/* Player/Team and Prop */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={cn('font-medium text-sm truncate', 'text-theme-text')}>
              {isTotals && matchupTeams
                ? `${matchupTeams.away?.abbr || '?'} @ ${matchupTeams.home?.abbr || '?'}`
                : isGameProp
                  ? leg.team
                  : leg.player}
            </span>
            {isGameProp && !isTotals && opponentAbbr && (
              <span className={cn('text-xs', 'text-theme-text-secondary')}>vs {opponentAbbr}</span>
            )}
            {!isGameProp && teamData && 'abbr' in teamData && (
              <span className={cn('text-xs', 'text-theme-text-secondary')}>
                {teamData.abbr}
                {opponentAbbr ? ` vs ${opponentAbbr}` : ''}
              </span>
            )}
            {/* Source indicator */}
            {leg.source === 'ai' && (
              <span className="text-[10px] bg-theme-primary/30 text-blue-400 px-1 rounded">AI</span>
            )}
            {/* Edge indicator */}
            {leg.aiEdge && (
              <span
                className={`text-[10px] px-1 rounded font-medium ${
                  leg.aiEdge === 'Strong'
                    ? 'bg-theme-success/30 text-green-400'
                    : leg.aiEdge === 'Moderate'
                      ? 'bg-theme-warning/30 text-yellow-400'
                      : 'bg-theme-surface/30 text-theme-text-tertiary'
                }`}
                title={`${leg.aiEdge} edge - AI's confidence in value`}
              >
                {leg.aiEdge === 'Strong' ? '↑↑' : leg.aiEdge === 'Moderate' ? '↑' : '~'}{' '}
                {leg.aiEdge}
              </span>
            )}
            {/* Trend badge (Hot/Cold/Consistent) */}
            {leg.trend && (
              <span
                className={`text-[10px] px-1 rounded font-medium cursor-help ${
                  leg.trend === 'Hot'
                    ? 'bg-orange-900/50 text-orange-400'
                    : leg.trend === 'Cold'
                      ? 'bg-blue-900/50 text-blue-400'
                      : 'bg-theme-surface/30 text-theme-text-tertiary'
                }`}
                title={
                  leg.trend === 'Hot'
                    ? 'Hot: Player trending up (L5 > L10 > L20). Recent performance exceeds season average.'
                    : leg.trend === 'Cold'
                      ? 'Cold: Player trending down (L5 < L10 < L20). Recent performance below season average.'
                      : 'Consistent: Stable performance across all time periods (L5 ≈ L10 ≈ L20).'
                }
              >
                {leg.trend === 'Hot' ? '🔥' : leg.trend === 'Cold' ? '❄️' : '➡️'}
              </span>
            )}
            {/* ML Anchor badge (Phase 9.5) */}
            {isAnchor && (
              <span
                className="text-[10px] px-1 rounded font-bold bg-purple-900/50 text-purple-400 border border-purple-500/30"
                title="Anchor leg — high confidence, appears in multiple optimizer parlays"
              >
                ANCHOR
              </span>
            )}
            {/* ML Score badge (Phase 9.1) */}
            {mlScore != null && (
              <span
                className={`text-[10px] px-1 rounded font-medium ${
                  mlScore >= 70
                    ? 'bg-green-900/50 text-green-400'
                    : mlScore >= 50
                      ? 'bg-yellow-900/50 text-yellow-400'
                      : 'bg-theme-surface/30 text-theme-text-tertiary'
                }`}
                title={`ML Score: ${mlScore}/100`}
              >
                ML {mlScore}
              </span>
            )}
            {/* CLV indicator (Phase 9.7) */}
            {clvPoints != null && clvPoints !== 0 && (
              <span
                className={`text-[10px] px-1 rounded font-medium ${
                  clvPoints > 0 ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'
                }`}
                title={`Line moved ${clvPoints > 0 ? '+' : ''}${clvPoints.toFixed(1)} since opening`}
              >
                CLV {clvPoints > 0 ? '+' : ''}
                {clvPoints.toFixed(1)}
              </span>
            )}
          </div>
          <div className={cn('text-sm truncate', 'text-theme-text-secondary')}>{propDisplay}</div>
        </div>

        {/* Odds */}
        <span className={cn('font-medium text-sm whitespace-nowrap', 'text-theme-success')}>
          {leg.odds}
        </span>

        {/* Chart button (shown when data is available or can be fetched) */}
        {canShowChart && onChartClick && (
          <button
            type="button"
            onClick={handleChartClick}
            className={cn(
              'p-1 rounded-lg transition',
              'bg-theme-surface hover:bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text',
            )}
            title={isGameProp ? 'View team history' : 'View stats chart'}
          >
            <BarChart3 className="w-4 h-4" />
          </button>
        )}

        {/* Roster Intel button */}
        {leg.team && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const { openGameModal } = useGlobalShortcutsStore.getState();
              const abbr = teamData && 'abbr' in teamData ? teamData.abbr : leg.team || '';
              openGameModal('lineup', undefined, [], abbr, opponentAbbr ?? undefined);
            }}
            className={cn(
              'p-1 rounded-lg transition',
              'bg-theme-surface hover:bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text',
            )}
            title="View team intel"
          >
            <ClipboardList className="w-4 h-4" />
          </button>
        )}

        {/* Expand/Collapse indicator (if has context) */}
        {hasContext && (
          <motion.span
            className="p-1 text-theme-text-secondary"
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <ChevronDown className="w-4 h-4" />
          </motion.span>
        )}

        {/* Remove button */}
        <button
          type="button"
          data-testid="builder-leg-remove-button"
          onClick={handleRemoveClick}
          disabled={disabled}
          className={cn(
            'p-1 rounded transition',
            disabled
              ? 'text-theme-text-tertiary cursor-not-allowed'
              : 'hover:bg-theme-danger/20 text-theme-text-tertiary hover:text-red-400',
          )}
          title={disabled ? 'Cannot modify while analyzing' : 'Remove from parlay'}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Expandable context section */}
      {
        <AnimatePresence initial={false}>
          {hasContext && isExpanded && (
            <motion.div
              key="leg-context"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              {renderExpandedContext()}
            </motion.div>
          )}
        </AnimatePresence>
      }
    </div>
  );
}
